import React, { ChangeEvent, memo, useEffect, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CheckboxProps,
    FormControl,
    FormControlLabel,
    InputLabel,
    OutlinedInput,
    styled,
    TextField,
    Tooltip,
    useTheme,
} from '@mui/material';
import { BaseModal, Loader } from '@tymely/components';
import {
    useArgumentsMetadataQuery,
    useCreateTandemReportMutation,
    useDecisionLlmClustersQuery,
    usePromptQuery,
    useSetAlert,
    useUser,
} from '@tymely/services';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { numbersStringToNumberArray } from '@tymely/utils';
import { IArgumentMetadata } from '@tymely/atoms';
import { CLUSTERING_COMMENT_WORKFLOWS } from '@tymely/utils/constants';

const StyledCheckbox = styled((props: CheckboxProps) => <Checkbox {...props} color="primary" />)(
    ({ theme }) => `
    margin-right: ${theme.spacing(1.5)};
`,
);

interface TandemReportDialogProperties {
    open: boolean;
    organizationId: number;
    intentId: number | null;
    clusterWorkflows?: boolean;
    scoreResponses?: boolean;
    joinArgumentsData?: boolean;
    onlyWithOriginalResponse?: boolean;
    ticketIds?: string;
    forceEvaluateArgNames?: string[];
    onClose: (reportId?: string) => void;
}

export const TandemReportDialog = memo((props: TandemReportDialogProperties) => {
    const theme = useTheme();
    const user = useUser();
    const setAlert = useSetAlert();

    const allDecisionLlmClusters = useDecisionLlmClustersQuery(props.organizationId, props.intentId ?? 0);
    const [forceEvaluateArgs, setForceEvaluateArgs] = useState<IArgumentMetadata[]>();
    const [decisionLlmClusters, setDecisionLlmClusters] = useState<[string, string][]>();
    const [numberOfTickets, setNumberOfTickets] = useState<number>();
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(new Date());

    const [ticketIds, setTicketIds] = useState<string | undefined>(props.ticketIds);

    const [isLoading, setIsLoading] = useState(false);
    const [clusterWorkflows, setClusterWorkflows] = useState(props.clusterWorkflows ?? true);
    const [scoreResponses, setScoreResponses] = useState(props.scoreResponses ?? true);
    const [joinArgumentsData, setJoinArgumentsData] = useState(props.joinArgumentsData ?? true);
    const [onlyWithOriginalResponse, setOnlyWithOriginalResponse] = useState(props.onlyWithOriginalResponse ?? false);
    const [intentIds, setIntentIds] = useState<string | null>(null);

    const [newDecisionName, setNewDecisionName] = useState('');
    const [newDecisionDescription, setNewDecisionDescription] = useState('');

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const regex = /^[0-9\b]+$/;
        if (e.target.value === '' || regex.test(e.target.value)) {
            const ticketsNumber = Number(e.target.value);
            setNumberOfTickets(ticketsNumber);
        }
    };

    const [clusteringPromptOverride, setClusteringPromptOverride] = useState<string | null>(null);
    const { data: defaultClusteringPrompt, isFetching: loadingClusteringPrompt } = usePromptQuery({
        name: CLUSTERING_COMMENT_WORKFLOWS,
    });

    useEffect(() => {
        if (defaultClusteringPrompt && defaultClusteringPrompt?.prompt)
            setClusteringPromptOverride(defaultClusteringPrompt.prompt);
    }, [defaultClusteringPrompt]);

    const createTandemReport = useCreateTandemReportMutation();

    const onCreate = async () => {
        if (numberOfTickets || ticketIds) {
            setIsLoading(true);

            try {
                const report = await createTandemReport.mutateAsync({
                    organization_id: props.organizationId,
                    policy_set_ids: props.intentId ? [props.intentId] : numbersStringToNumberArray(intentIds || ''),
                    tickets_number: numberOfTickets,
                    start_date: startDate?.toISOString(),
                    end_date: endDate?.toISOString(),
                    send_to: user ? [user.email] : [],
                    cluster_workflows: clusterWorkflows,
                    score_responses: scoreResponses,
                    join_arguments_data: joinArgumentsData,
                    only_with_original_response: onlyWithOriginalResponse,
                    ticket_ids: numbersStringToNumberArray(ticketIds || ''),
                    clustering_prompt_override: clusteringPromptOverride ?? defaultClusteringPrompt?.prompt,
                    force_evaluate_arg_names:
                        forceEvaluateArgs && forceEvaluateArgs.length > 0
                            ? forceEvaluateArgs.map((arg) => arg.name)
                            : undefined,
                    initial_wf_descriptions: decisionLlmClusters,
                    use_step_functions: true,
                });

                setIsLoading(false);
                setAlert(`Tandem Report is being created. You'll receive an email when it's ready.`);
                props.onClose(report.report_id);
            } catch (err: any) {
                setIsLoading(false);
                setAlert(`Failed to create tandem report. ${err.message}`, 'error');
                props.onClose();
            }
        }
    };

    const argsMetadataQuery = useArgumentsMetadataQuery();

    const argOptions = React.useMemo(() => {
        if (!argsMetadataQuery.data) {
            return [];
        }
        return argsMetadataQuery.data.filter((md) => ['TEXT_ARGUMENT', 'SYSTEM_ARGUMENT'].includes(md.arg_type));
    }, [argsMetadataQuery.data]);

    useEffect(() => {
        if (!argOptions || forceEvaluateArgs) {
            return;
        }
        if (!props.forceEvaluateArgNames) {
            setForceEvaluateArgs([]);
            return;
        }
        setForceEvaluateArgs(argOptions.filter((arg) => props.forceEvaluateArgNames!.includes(arg.name)));
    }, [argOptions, forceEvaluateArgs, props.forceEvaluateArgNames, setForceEvaluateArgs]);

    return (
        <BaseModal
            loading={isLoading}
            open={props.open}
            maxWidth="lg"
            fullHeight={true}
            title="Create Tandem Report"
            onOk={onCreate}
            disabled={
                !numberOfTickets &&
                (!startDate || Number.isNaN(startDate.getTime()) || !endDate || Number.isNaN(endDate.getTime())) &&
                !ticketIds &&
                'Must define number of tickets, date range or ticket-ids.'
            }
            onClose={() => props.onClose()}
        >
            <Box
                sx={{
                    minHeight: theme.spacing(10),
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                }}
            >
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <TextField
                        id="ticketsNumber"
                        label="Number of tickets for report"
                        type="number"
                        value={numberOfTickets}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e)}
                    />
                    {!props.intentId && (
                        <TextField
                            id="Intent ids"
                            label="Intent ids"
                            placeholder="Enter comma sepearated number of intent ids for report"
                            sx={{ marginTop: theme.spacing(2) }}
                            value={intentIds}
                            onChange={(intentIds) => setIntentIds(intentIds.target.value)}
                        />
                    )}
                    <DatePicker
                        label="Start date"
                        sx={{ marginTop: theme.spacing(2) }}
                        value={startDate}
                        onChange={(date) => setStartDate(date || null)}
                        format={'dd/MM/yyyy'}
                        enableAccessibleFieldDOMStructure={false}
                    />
                    <DatePicker
                        label="End date"
                        sx={{ marginTop: theme.spacing(2) }}
                        value={endDate}
                        onChange={(date) => setEndDate(date || null)}
                        format={'dd/MM/yyyy'}
                        enableAccessibleFieldDOMStructure={false}
                    />
                </LocalizationProvider>
                {argOptions.length > 0 && forceEvaluateArgs ? (
                    <Autocomplete
                        multiple
                        options={argOptions}
                        getOptionLabel={(option) => option.name}
                        renderInput={(params) => (
                            <TextField {...params} variant="outlined" label="Force Evaluate Arguments" />
                        )}
                        value={forceEvaluateArgs}
                        onChange={(_event, newValue) => setForceEvaluateArgs(newValue)}
                        sx={{ marginTop: theme.spacing(2) }}
                    />
                ) : (
                    <Loader />
                )}
                {props.intentId &&
                    (decisionLlmClusters && allDecisionLlmClusters.data ? (
                        <Box sx={{ marginTop: theme.spacing(3) }}>
                            <FormControl>
                                <InputLabel htmlFor="decision-llm-clustering" shrink>
                                    Decisions LLM clusters
                                </InputLabel>
                                <OutlinedInput
                                    id="decision-llm-clustering"
                                    notched
                                    label="Decisions LLM clusters"
                                    inputComponent={() => (
                                        <Autocomplete
                                            multiple
                                            fullWidth
                                            options={allDecisionLlmClusters.data}
                                            getOptionLabel={(option) => option[0]}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    InputProps={{ ...params.InputProps, disableUnderline: true }}
                                                    variant="standard"
                                                    size="small"
                                                    sx={{ mt: 1 }}
                                                />
                                            )}
                                            value={decisionLlmClusters}
                                            onChange={(_event, newValue) => setDecisionLlmClusters(newValue)}
                                            sx={{
                                                pr: 1,
                                                '& .MuiInputBase-root': {
                                                    minHeight: 38,
                                                    maxHeight: 180,
                                                    overflowY: 'auto',
                                                    overflowX: 'clip',
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </FormControl>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    marginTop: theme.spacing(1),
                                    width: '100%',
                                    gap: theme.spacing(1),
                                }}
                            >
                                <TextField
                                    label="New decision name"
                                    fullWidth
                                    size="small"
                                    value={newDecisionName}
                                    onChange={(e) => setNewDecisionName(e.target.value)}
                                />
                                <TextField
                                    label="New decision description"
                                    fullWidth
                                    size="small"
                                    value={newDecisionDescription}
                                    onChange={(e) => setNewDecisionDescription(e.target.value)}
                                />
                                <Button
                                    disabled={newDecisionName === ''}
                                    onClick={() => {
                                        setDecisionLlmClusters((oldDecisionLlmClusters) => [
                                            ...(oldDecisionLlmClusters ?? []).filter(
                                                ([decisionName]) => decisionName !== newDecisionName,
                                            ),
                                            [newDecisionName, newDecisionDescription],
                                        ]);
                                        setNewDecisionName('');
                                        setNewDecisionDescription('');
                                    }}
                                >
                                    Add
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <Loader />
                    ))}
                <TextField
                    label="Ticket IDs"
                    placeholder="Enter ticket IDs separated by commas or spaces"
                    sx={{ marginTop: theme.spacing(3) }}
                    multiline
                    rows={10}
                    value={ticketIds}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setTicketIds(e.target.value)}
                />

                {loadingClusteringPrompt ? (
                    <Loader />
                ) : (
                    <TextField
                        placeholder="Enter prompt to override the default clustering prompt. If empty, the default prompt will be used. Your starting point can be CLUSTERING_COMMENT_WORKFLOWS prompt."
                        sx={{ marginTop: theme.spacing(2) }}
                        multiline
                        rows={20}
                        value={clusteringPromptOverride}
                        disabled={!clusterWorkflows}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setClusteringPromptOverride(e.target.value)}
                    />
                )}

                <Tooltip
                    title="Cluster comments into workflows following the CLUSTERING_COMMENT_WORKFLOWS
                     LLM template."
                >
                    <FormControlLabel
                        control={
                            <StyledCheckbox
                                checked={clusterWorkflows}
                                onChange={() => setClusterWorkflows(!clusterWorkflows)}
                            />
                        }
                        label="Cluster workflows"
                        sx={{ mt: 2, ml: 0.5 }}
                    />
                </Tooltip>
                <Tooltip
                    title="The divergence between the Tymely response and the original client response will
                        be analyzed, scored, and the variances will be displayed.
                        The template used for the LLM is SCORE_DIFFERENCE_BETWEEN_RESPONSES."
                >
                    <FormControlLabel
                        control={
                            <StyledCheckbox
                                checked={scoreResponses}
                                onChange={() => setScoreResponses(!scoreResponses)}
                            />
                        }
                        label="Score responses"
                        sx={{ ml: 0.5 }}
                    />
                </Tooltip>
                <Tooltip title="System arguments from the ticket first version of will be added to the report">
                    <FormControlLabel
                        control={
                            <StyledCheckbox
                                checked={joinArgumentsData}
                                onChange={() => setJoinArgumentsData(!joinArgumentsData)}
                            />
                        }
                        label="Join arguments data"
                        sx={{ ml: 0.5 }}
                    />
                </Tooltip>
                <Tooltip title="Choose only tickets that have original responses to the customer inquiry">
                    <FormControlLabel
                        control={
                            <StyledCheckbox
                                checked={onlyWithOriginalResponse}
                                onChange={() => setOnlyWithOriginalResponse((v) => !v)}
                            />
                        }
                        label="Tickets with original response"
                        sx={{ ml: 0.5 }}
                    />
                </Tooltip>
            </Box>
        </BaseModal>
    );
});

TandemReportDialog.displayName = 'TandemReportDialog';
